import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useState, useEffect, useCallback, useRef } from 'react';
import { callUIAPI } from '../../../utils/apiUtils';
import * as apiConstants from '../../../utils/apiUtilsConstants';
import FirmPaymentId from './FirmPaymentId';
import FirmProfile from './FirmProfile';
import FirmSubscription from './FirmSubscription';
import FirmAiBudget from './FirmAiBudget';
import FirmNotes from './FirmNotes';
import FirmDangerZone from './FirmDangerZone';
import FirmUsers from './FirmUsers';
import './Firm.css';

const STATUS_COLORS = {
  TRIAL:     { bg: '#dbeafe', text: '#1e40af' },
  ACTIVE:    { bg: '#dcfce7', text: '#166534' },
  EXPIRED:   { bg: '#fef9c3', text: '#854d0e' },
  SUSPENDED: { bg: '#fee2e2', text: '#991b1b' },
  DELETED:   { bg: '#f3f4f6', text: '#6b7280' },
};

const TAB_GENERAL = 'general';
const TAB_USERS   = 'users';

const Firm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [firm, setFirm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(
    searchParams.get('tab') || location.state?.tab || TAB_GENERAL
  );

  const subscriptionRef = useRef(null);

  const fetchFirm = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await callUIAPI(`bo/firms/${id}`, null, apiConstants.METHOD_GET, true);
      setFirm(response.data.item);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load firm.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) fetchFirm();
  }, [id, fetchFirm]);

  const handleCustomerChanged = useCallback(() => {
    subscriptionRef.current?.refresh();
  }, []);

  if (loading) {
    return (
      <div className="firm-page">
        <div className="firm-card">
          <div className="firm-skeleton-title" />
          <div className="firm-skeleton-line" />
          <div className="firm-skeleton-line-short" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="firm-page">
        <div className="firm-card firm-error-card">
          <p className="firm-error-text">{error}</p>
          <button className="firm-btn-retry" onClick={fetchFirm}>Retry</button>
        </div>
      </div>
    );
  }

  if (!firm) return null;

  const statusColors = STATUS_COLORS[firm.status] || { bg: '#f3f4f6', text: '#374151' };

  return (
    <div className="firm-page">

      {}
      <div className="firm-card firm-header-card">
        <div className="firm-header">
          <div>
            <h2 className="firm-name">{firm.name}</h2>
            <span
              className="firm-status-badge"
              style={{ backgroundColor: statusColors.bg, color: statusColors.text }}
            >
              {firm.status}
            </span>
          </div>
          <span className="firm-id">ID: {firm.id}</span>
        </div>

        <div className="firm-tabs">
          <button
            className={`firm-tab ${activeTab === TAB_GENERAL ? 'firm-tab-active' : ''}`}
            onClick={() => setActiveTab(TAB_GENERAL)}
          >
            General
          </button>
          <button
            className={`firm-tab ${activeTab === TAB_USERS ? 'firm-tab-active' : ''}`}
            onClick={() => setActiveTab(TAB_USERS)}
          >
            Users
          </button>
        </div>
      </div>

      {}
      {activeTab === TAB_GENERAL && (
        <div className="firm-card">
          <FirmProfile
            firm={firm}
            onFirmUpdate={setFirm}
          />
          <div className="firm-divider" />
          <FirmPaymentId
            firm={firm}
            onFirmUpdate={setFirm}
            onCustomerChanged={handleCustomerChanged}
          />
          <div className="firm-divider" />
          <FirmSubscription
            ref={subscriptionRef}
            firmId={id}
            firmName={firm.name}
            hasPaymentCustomer={!!firm.paymentCustomerId}
          />
          <div className="firm-divider" />
          <FirmAiBudget firm={firm} onFirmUpdate={setFirm} />
          <div className="firm-divider" />
          <FirmNotes firmId={id} />
          <div className="firm-divider" />
          <FirmDangerZone firm={firm} onDeleted={() => navigate('/bo/firms')} />
        </div>
      )}

      {}
      {activeTab === TAB_USERS && (
        <FirmUsers firmId={id} />
      )}
    </div>
  );
};

export default Firm;